import {
  ControlledCheckbox,
  ControlledDateOfBirthInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { useDateOfInjury } from "components/DOIHeaderSelect/useServiceDOI";
import { MaskedInput, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import { GENDERS } from "constants/FormConstants";
import { useMemo, useEffect, useState } from "react";
import type { Path } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { DocumentManagementDataType } from "types/DataTypes";
import { useSelector } from "utils/redux";

type Props = {
  onSave: (field: string | null, value: unknown) => void;
  service: DocumentManagementDataType;
  doiId: number | null;
  className?: string;
};

type FormValues = {
  "claimant.first_name": string;
  "claimant.last_name": string;
  "date_of_injury.jurisdiction": string;
  "date_of_injury.wcmsa_zip_code": string;
  "date_of_injury.one_eleven_jurisdiction": string;
  report_date?: string | Date;
  "claimant.ssn": string;
  "claimant.medicare_id": string;
  "claimant.address": string;
  "claimant.state": string;
  "claimant.zip_code": string;
  "claimant.birth_date": string | Date | null;
  "claimant.gender": string;
  "claimant.is_medicare_eligible": boolean;
  "claimant.reasonable_expectation": boolean;
};

const DocumentManagementClaimantInformation = ({
  onSave,
  service,
  doiId,
  className = "",
}: Props) => {
  const constants = useSelector((state) => state.shared.constants);
  const { data: selectedDOI } = useDateOfInjury(doiId);
  const [selectedDoiId, setSelectedDoiId] = useState<number | null>(null);

  const {
    control,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      "claimant.first_name": service?.claimant?.first_name || "",
      "claimant.last_name": service?.claimant?.last_name || "",
      "date_of_injury.jurisdiction": selectedDOI?.jurisdiction || "",
      "date_of_injury.wcmsa_zip_code": selectedDOI?.wcmsa_zip_code || "",
      "date_of_injury.one_eleven_jurisdiction":
        selectedDOI?.one_eleven_jurisdiction || "",
      report_date: "",
      "claimant.ssn": service?.claimant?.ssn || "",
      "claimant.medicare_id": service?.claimant?.medicare_id || "",
      "claimant.address": service?.claimant?.address || "",
      "claimant.state": service?.claimant?.state || "",
      "claimant.zip_code": service?.claimant?.zip_code || "",
      "claimant.birth_date": service?.claimant?.birth_date || null,
      "claimant.gender": service?.claimant?.gender || "",
      "claimant.is_medicare_eligible":
        service?.claimant?.is_medicare_eligible || false,
      "claimant.reasonable_expectation":
        service?.claimant?.reasonable_expectation || false,
    },
  });

  useEffect(() => {
    if (selectedDOI) {
      setSelectedDoiId(selectedDOI.id);
      const doiFields = {
        "date_of_injury.jurisdiction": selectedDOI.jurisdiction,
        "date_of_injury.wcmsa_zip_code": selectedDOI.wcmsa_zip_code,
        "date_of_injury.one_eleven_jurisdiction":
          selectedDOI.one_eleven_jurisdiction,
      } as const;

      Object.entries(doiFields).forEach(([field, value]) => {
        setValue(field as Path<FormValues>, value ?? "");
      });
    }
  }, [selectedDOI, setValue]);

  const handleSave = async (field: string, value: unknown) => {
    onSave(field, value);
    return Promise.resolve();
  };

  const claimantInfoContent = useMemo(
    () => (
      <>
        <FutureMedicalCard heading="Claimant Information">
          <ControlledInput
            control={control}
            name="claimant.first_name"
            label="First Name"
            errors={errors}
            onSave={(value) => handleSave("claimant.first_name", value)}
            componentType={TextInput}
          />
          <ControlledInput
            control={control}
            name="claimant.last_name"
            label="Last Name"
            errors={errors}
            onSave={(value) => handleSave("claimant.last_name", value)}
            componentType={TextInput}
          />
          <ControlledSelect
            key={`jurisdiction-${selectedDoiId}`}
            control={control}
            name="date_of_injury.jurisdiction"
            label="WCMSA State"
            errors={errors}
            options={constants?.STATES || []}
            onSave={(option) =>
              handleSave("date_of_injury.jurisdiction", option?.value)
            }
          />
          <ControlledInput
            key={`wcmsa-zip-${selectedDoiId}`}
            control={control}
            name="date_of_injury.wcmsa_zip_code"
            label="WCMSA Zip Code"
            errors={errors}
            onSave={(value) =>
              handleSave("date_of_injury.wcmsa_zip_code", value)
            }
            componentType={MaskedInput}
            mask="zipcode-nine"
          />
          <ControlledSelect
            key={`111-jurisdiction-${selectedDoiId}`}
            control={control}
            name="date_of_injury.one_eleven_jurisdiction"
            label="111 Jurisdiction"
            errors={errors}
            options={constants?.JURISDICTIONS || []}
            onSave={(option) =>
              handleSave(
                "date_of_injury.one_eleven_jurisdiction",
                option?.value,
              )
            }
          />
          <ControlledInput
            control={control}
            name="claimant.ssn"
            label="SSN"
            errors={errors}
            onSave={(value) => handleSave("claimant.ssn", value)}
            componentType={MaskedInput}
            mask="ssn"
          />
          <ControlledInput
            control={control}
            name="claimant.medicare_id"
            label="Medicare ID"
            errors={errors}
            onSave={(value) => handleSave("claimant.medicare_id", value)}
            componentType={MaskedInput}
            mask="medicare"
          />
          <ControlledInput
            control={control}
            name="claimant.address"
            label="Claimant's Address"
            errors={errors}
            onSave={(value) => handleSave("claimant.address", value)}
            componentType={TextInput}
          />
          <ControlledSelect
            control={control}
            name="claimant.state"
            label="State"
            errors={errors}
            options={constants?.STATES || []}
            onSave={(option) => handleSave("claimant.state", option?.value)}
          />
          <ControlledInput
            control={control}
            name="claimant.zip_code"
            label="Zip"
            errors={errors}
            onSave={(value) => handleSave("claimant.zip_code", value)}
            componentType={MaskedInput}
            mask="zipcode-nine"
          />
          <ControlledDateOfBirthInput
            control={control}
            name="claimant.birth_date"
            errors={errors}
            onSave={(value) => handleSave("claimant.birth_date", value)}
          />
          <ControlledSelect
            control={control}
            name="claimant.gender"
            label="Gender"
            errors={errors}
            options={GENDERS}
            onSave={(option) => handleSave("claimant.gender", option?.value)}
          />
          <ControlledCheckbox
            control={control}
            name="claimant.is_medicare_eligible"
            label="Is Medicare Eligible"
            errors={errors}
            onSave={(value) =>
              handleSave("claimant.is_medicare_eligible", value)
            }
          />
          <ControlledCheckbox
            control={control}
            name="claimant.reasonable_expectation"
            label="Reasonable Expectation"
            errors={errors}
            onSave={(value) =>
              handleSave("claimant.reasonable_expectation", value)
            }
          />
        </FutureMedicalCard>
      </>
    ),
    [control, errors, constants, handleSave, selectedDoiId, service],
  );

  return (
    <div className={`DocumentManagementClaimantInformation ${className}`}>
      <div className="swim-headers">Claimant Information</div>
      <div className="swim-column">{claimantInfoContent}</div>
    </div>
  );
};

export default DocumentManagementClaimantInformation;
